import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { FileStack, Plus } from "lucide-react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { BlueprintListItem } from "./BlueprintListItem";
import { BlueprintEditorDialog } from "./BlueprintEditorDialog";
import { emptyEditor, useBlueprintsPanel } from "./useBlueprintsPanel";

type Props = {
  /** "catalog" — Builder-tab view; "drag" — compact draggable rows. */
  mode?: "catalog" | "drag";
};

export const BlueprintsPanel = ({ mode = "catalog" }: Props) => {
  const { active } = useWorkspace();
  const workspaceId = active?.id;
  const panel = useBlueprintsPanel(workspaceId);
  const { editor, blocks, blueprints, blocksByType, createMut, updateMut, deleteMut, duplicateMut } = panel;

  const isSaving = createMut.isPending || updateMut.isPending;
  const canSave = editor.title.trim().length > 0 && editor.blockRefs.length > 0;

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      {mode === "catalog" && (
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <FileStack className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-sm font-medium">Blueprints</p>
              <p className="text-xs text-muted-foreground">
                Saved stacks of blocks for repeatable publishing shapes. Drag any blueprint into your draft.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={panel.openCreate} disabled={!workspaceId}>
            <Plus className="h-3.5 w-3.5" />
            New blueprint
          </Button>
        </div>
      )}

      {blueprints.length === 0 ? (
        <div className="flex flex-col items-start gap-2 rounded-md border border-dashed border-border/60 p-4">
          <p className="text-xs text-muted-foreground">
            No blueprints yet. A blueprint stitches your reusable Blocks together into a repeatable publishing shape (e.g. "Weekly newsletter").
          </p>
          <Button size="sm" onClick={panel.openCreate} disabled={!workspaceId}>
            <Plus className="h-3.5 w-3.5" />
            Create your first blueprint
          </Button>
        </div>
      ) : (
        <Accordion type="multiple" className="flex-1 overflow-auto">
          {blueprints.map((bp) => (
            <BlueprintListItem
              key={bp.id}
              bp={bp}
              blocks={blocks}
              mode={mode}
              onEdit={panel.openEdit}
              onDuplicate={(b) => duplicateMut.mutate(b)}
              duplicatePending={duplicateMut.isPending}
              onDelete={(id) => deleteMut.mutate(id)}
            />
          ))}
        </Accordion>
      )}

      <BlueprintEditorDialog
        editor={editor}
        setEditor={panel.setEditor}
        blocks={blocks}
        blocksByType={blocksByType}
        emptyEditor={emptyEditor}
        onSave={() =>
          editor.editingId ? updateMut.mutate(editor) : createMut.mutate(editor)
        }
        isSaving={isSaving}
        canSave={canSave}
        addRef={panel.addRef}
        updateRef={panel.updateRef}
        removeRef={panel.removeRef}
        moveRef={panel.moveRef}
      />
    </div>
  );
};

export default BlueprintsPanel;
